import { useCallback, useState } from 'react'
import { Modal, useModals } from 'almanac-ui/Modal'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { useNoCampaignGuard } from '../hooks/useNoCampaignGuard'
import { NoCampaignModal } from '../NoCampaignModal/NoCampaignModal'
import { CreateCampaignModal } from 'screens/ContentPlan/CreateCampaignModal/CreateCampaignModal'
import { useHistory, generatePath } from 'react-router-dom'
import { CONTENT_PLAN_PATH, BRAND_KITS_PATH } from 'paths'
import { useAddToBrandPlanSchedule } from 'hooks/queries/schedules/useAddToBrandPlanSchedule'
import { usePreviewTopicsSync } from 'hooks/queries/schedules/usePreviewTopicsSync'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ApiError } from 'utils/error'
import {
  openWeeklyPostLimitModal,
  WEEKLY_POST_LIMIT_ERROR_CODE,
} from 'components/WeeklyPostLimitReachedModal/openWeeklyPostLimitModal'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useCreditsToGenerateContent } from 'hooks/useNeedsCreditsToGenerateContent'
import { usePurchaseContentCreditsModal } from 'hooks/usePurchaseContentCreditsModal'
import { Wizards } from 'components/Wizards/Wizards'
import { BrandPlan, BrandPlanSchedule } from 'types'
import { CampaignSelector } from './CampaignSelector'
import { ContentTypeSelector } from './ContentTypeSelector'
import { ContentCategory, CreateContentModalProps, CreateContentModalStep, TopicRow } from './Types'
import {
  buildInitialTopicRows,
  buildInitialTopicRowsFromCounts,
  expandTopicRowsToPayload,
  sampleTopicForPostType,
} from './utils'
import { ContentTypeSelectorWithCounts } from './ContentTypeSelectorWithCounts'
import { ContentTypeCounts } from 'utils/brandPlans/contentCategories'
import { PostsDetails } from './PostsDetails'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { CreateNewModal } from 'screens/ContentPlan/CreateNewModal/CreateNewModal'

const CreateContentModalInner = ({
  onSuccess,
  initialScheduleId,
  allowWizardsModal,
  initialContentCategory,
  discoveryMode,
  brandPlan,
}: CreateContentModalProps & { brandPlan: BrandPlan }) => {
  const workspaceId = useCurrentWorkspaceId()
  const { closeModal, openModal, closeAllModals } = useModals()
  const { mutate: addMoreContent, isLoading: isMutating } = useAddToBrandPlanSchedule()
  const openPurchaseModal = usePurchaseContentCreditsModal()
  const isStrictBrandControl = brandPlan.brand_kit?.strict_brand_control ?? false

  let initialStep: CreateContentModalStep = 'CampaignSelect'
  if (initialScheduleId) {
    brandPlan.post_content_types ? (initialStep = 'ContentTypeSelectWithCounts') : (initialStep = 'ConfigureContent')
  } else if (discoveryMode || (brandPlan.post_content_types && !initialContentCategory)) {
    initialStep = 'ContentTypeSelect'
  }

  const [step, setStep] = useState<CreateContentModalStep>(initialStep)
  const [selectedContentCategory, setSelectedContentCategory] = useState<ContentCategory | null>(
    initialContentCategory ?? null,
  )
  const [targetSchedule, setTargetSchedule] = useState<BrandPlanSchedule | undefined>(undefined)
  const [topicRows, setTopicRows] = useState<TopicRow[]>([])
  const [counts, setCounts] = useState<ContentTypeCounts>({
    still_image_post: 0,
    carousel_post: 0,
    feed_video_post: 0,
    short_form_video: 0,
    stories: 0,
    blog_post: 0,
    newsletter: 0,
  })

  const { mutateAsync: generatePreviewTopics } = usePreviewTopicsSync({
    onSuccess: (preview, variables) => {
      if (!preview.topics) return

      let hasUnfilled = false
      setTopicRows((prev) => {
        const usedTopics = new Set(prev.filter((r) => !r.loading && r.topic).map((r) => r.topic))
        return prev.map((row) => {
          if (!row.loading || row.userEdited) return { ...row, loading: false }
          const next = sampleTopicForPostType(preview.topics ?? {}, row.postType, usedTopics)
          if (next) {
            usedTopics.add(next)
            return { ...row, topic: next, loading: false }
          }
          hasUnfilled = true
          return row
        })
      })
      if (hasUnfilled && variables?.scheduleId) {
        setTimeout(() => triggerTopicGeneration(variables.scheduleId), 0)
      }
    },
    onError: () => {
      setTopicRows((prev) => prev.map((row) => (row.loading ? { ...row, loading: false } : row)))
    },
  })

  const triggerTopicGeneration = useCallback(
    (scheduleId: number) => {
      generatePreviewTopics({ scheduleId })
    },
    [generatePreviewTopics],
  )

  const totalContentCount = topicRows.length

  const socialCount = topicRows.filter((r) => r.postType === 'social_post').length
  const videoCount = topicRows.filter((r) => r.postType === 'video').length
  const blogCount = topicRows.filter((r) => r.postType === 'blog_post').length
  const emailCount = topicRows.filter((r) => r.postType === 'email').length

  const stillImageCount = topicRows.filter((r) => r.contentCategory === 'still_image_post').length
  const carouselCount = topicRows.filter((r) => r.contentCategory === 'carousel_post').length
  const feedVideoCount = topicRows.filter((r) => r.contentCategory === 'feed_video_post').length
  const storyCount = topicRows.filter((r) => r.contentCategory === 'stories').length
  const shortFormFromCategory = topicRows.filter((r) => r.contentCategory === 'short_form_video').length
  const undifferentiatedSocial = topicRows.filter((r) => r.postType === 'social_post' && !r.contentCategory).length
  const undifferentiatedVideo = topicRows.filter((r) => r.postType === 'video' && !r.contentCategory).length

  // When a specific social sub-type is selected, pass explicit counts so weight distribution is skipped
  const isSocialSubtypeSelected =
    selectedContentCategory !== null &&
    ['still_image_post', 'carousel_post', 'feed_video_post', 'stories'].includes(selectedContentCategory)
  const explicitStillImagesSingle = selectedContentCategory === 'still_image_post' ? totalContentCount : 0
  const explicitCarouselsSingle = selectedContentCategory === 'carousel_post' ? totalContentCount : 0
  const explicitVideosSingle = selectedContentCategory === 'feed_video_post' ? totalContentCount : 0
  const explicitStoriesSingle = selectedContentCategory === 'stories' ? totalContentCount : 0

  const explicitSubtypeRowCount = stillImageCount + carouselCount + feedVideoCount + storyCount + shortFormFromCategory
  const usePerCategoryCredits = isSocialSubtypeSelected || explicitSubtypeRowCount > 0

  const socialCountForCredits = usePerCategoryCredits ? undifferentiatedSocial : socialCount
  const shortFormVideoForCredits = usePerCategoryCredits ? shortFormFromCategory + undifferentiatedVideo : videoCount
  const explicitStillImages = isSocialSubtypeSelected ? explicitStillImagesSingle : stillImageCount
  const explicitCarousels = isSocialSubtypeSelected ? explicitCarouselsSingle : carouselCount
  const explicitVideos = isSocialSubtypeSelected ? explicitVideosSingle : feedVideoCount
  const explicitStories = isSocialSubtypeSelected ? explicitStoriesSingle : storyCount

  const { needsCredits, creditsNeeded, autopilotContentCreditsPrice } = useCreditsToGenerateContent(
    socialCountForCredits,
    blogCount,
    emailCount,
    shortFormVideoForCredits,
    explicitStillImages,
    explicitVideos,
    explicitCarousels,
    explicitStories,
  )

  const handleOneOffContent =
    allowWizardsModal && !brandPlan.has_content_strategies
      ? () => {
          closeModal()
          openModal(Wizards, {})
        }
      : undefined

  const handleContentTypeSelect = (category: ContentCategory) => {
    setSelectedContentCategory(category)
    setStep('CampaignSelect')
  }

  const handleContentTypeSelectWithCounts = (schedule?: BrandPlanSchedule) => {
    if (initialScheduleId && schedule) {
      setTargetSchedule(schedule)
      setTopicRows(buildInitialTopicRowsFromCounts(schedule, counts))
      triggerTopicGeneration(schedule.id)
      setStep('ReviewPostTopics')
    } else {
      setStep('CampaignSelect')
    }
  }

  const handleCampaignConfirm = (schedule: BrandPlanSchedule, postCount: number) => {
    setTargetSchedule(schedule)
    setTopicRows(buildInitialTopicRows(schedule, postCount, selectedContentCategory ?? undefined))
    triggerTopicGeneration(schedule.id)
    setStep('ConfigureContent')
  }

  const handleCreateContent = () => {
    if (needsCredits) {
      if (autopilotContentCreditsPrice) {
        openPurchaseModal({ price: autopilotContentCreditsPrice })
      } else {
        showToastMessage('Unable to determine credit pricing. Please try again.', 'error')
      }
      return
    }

    if (!targetSchedule) {
      showToastMessage(
        'Unable to schedule your content. Try again. Please contact support if the problem persists.',
        'error',
      )
      return
    }

    const payload = expandTopicRowsToPayload(topicRows)

    addMoreContent(
      {
        schedule: payload,
        brandPlanId: brandPlan.id,
        brandPlanScheduleId: targetSchedule.id,
      },
      {
        onSuccess: () => {
          onSuccess?.(totalContentCount, targetSchedule.id)
          closeModal()
        },
        onError: (error) => {
          if (error instanceof ApiError && error.errorCode === 'no_brand_kit_media') {
            const brandKitPath = generatePath(BRAND_KITS_PATH, { workspaceId }) + '#images_videos'
            showToastMessage(
              'Your content preferences require brand kit media, but none are uploaded.',
              'error',
              { type: 'link', to: brandKitPath, label: 'Go to Brand Kit', variant: 'proceed' },
              10,
            )
          } else if (error instanceof ApiError && error.errorCode === WEEKLY_POST_LIMIT_ERROR_CODE) {
            openWeeklyPostLimitModal(openModal, closeAllModals)
          } else {
            showErrorMessageToast(error instanceof Error ? error.message : 'An error occurred while adding content.')
          }
        },
      },
    )
  }

  const campaignSelectorOnBack = brandPlan.post_content_types ? () => setStep('ContentTypeSelect') : undefined

  return (
    <Modal.Root size="md">
      {step === 'ContentTypeSelect' && (
        <ContentTypeSelector
          onSelect={handleContentTypeSelect}
          isStrictBrandControl={isStrictBrandControl}
          discoveryMode={discoveryMode}
        />
      )}
      {step === 'ContentTypeSelectWithCounts' && initialScheduleId && (
        <ContentTypeSelectorWithCounts
          onNext={handleContentTypeSelectWithCounts}
          scheduleId={initialScheduleId}
          counts={counts}
          setCounts={setCounts}
          isStrictBrandControl={isStrictBrandControl}
        />
      )}
      {step === 'CampaignSelect' && (
        <CampaignSelector
          brandPlanId={brandPlan.id}
          onConfirm={handleCampaignConfirm}
          onBack={campaignSelectorOnBack}
          onOneOff={handleOneOffContent}
        />
      )}
      {step === 'ConfigureContent' && !targetSchedule && <Modal.Header title="Configure Posts" />}
      {['ReviewPostTopics', 'ConfigureContent'].includes(step) && targetSchedule && (
        <PostsDetails
          topicRows={topicRows}
          setTopicRows={setTopicRows}
          targetSchedule={targetSchedule}
          onBack={() =>
            step === 'ReviewPostTopics' ? setStep('ContentTypeSelectWithCounts') : setStep('CampaignSelect')
          }
          onConfirm={handleCreateContent}
          isLoading={isMutating}
          needsCredits={needsCredits}
          creditsNeeded={creditsNeeded}
        />
      )}
    </Modal.Root>
  )
}

export const CreateContentModalWrapper = (props: CreateContentModalProps) => {
  const isBlankPostEnabled = useFlipperFlag('blank_post_creation')

  if (isBlankPostEnabled) return <CreateNewModal />

  return <LegacyCreateContentModalWrapper {...props} />
}

const LegacyCreateContentModalWrapper = (props: CreateContentModalProps) => {
  const { data: brandPlan } = useCurrentBrandPlan()
  const { openModal, closeModal } = useModals()
  const history = useHistory()
  const workspaceId = useCurrentWorkspaceId()
  const { isLoading, hasNoCampaigns } = useNoCampaignGuard(brandPlan?.id)

  if (!brandPlan || isLoading) return null

  if (hasNoCampaigns) {
    return (
      <NoCampaignModal
        onCancel={() => closeModal()}
        onCreateCampaign={() => {
          closeModal()
          openModal(CreateCampaignModal, {
            brandPlan,
            onSuccess: () => {
              history.push(generatePath(CONTENT_PLAN_PATH, { workspaceId }))
            },
          })
        }}
      />
    )
  }

  return <CreateContentModalInner {...props} brandPlan={brandPlan} />
}

export { CreateContentModalWrapper as CreateContentModal }
